import { useRef, useEffect } from 'react';

import { Link } from 'react-router-dom';

import { Button } from '../../components/common/Button';
import { MarketingSiteHeader } from '../../components/layout/MarketingSiteHeader';
import { ROUTES } from '../../constants';
import { useAppSelector } from '../../store/hooks';
import { getDashboardHref, getSignInHref } from '../../utils/getSignInHref';

import {
  AI_PIPELINE_STEPS,
  HERO_PILLARS,
  PLATFORM_SUITE,
  ROLE_BREAKDOWN,
} from './homeContent';
import { HomeLifecycleSection } from './HomeLifecycleSection';
import {
  getDemoScheduleHref,
  HOME_META_DESCRIPTION,
  HOME_PAGE_TITLE,
  PLATFORM_SUITE_ANCHOR,
} from './homeMarketing';
import { HomeRouteMap } from './HomeRouteMap';
import { useHomePageMotion } from './useHomePageMotion';

export function HomePage() {
  const rootRef = useRef<HTMLElement>(null);
  const routeMapRef = useRef<React.ComponentRef<typeof HomeRouteMap>>(null);
  const suiteRef = useRef<HTMLElement>(null);
  const lifecycleSectionRef = useRef<HTMLElement>(null);
  const rolesRef = useRef<HTMLElement>(null);
  const aiRef = useRef<HTMLElement>(null);
  const { isAuthenticated } = useAppSelector(state => state.auth);

  const { activeStage } = useHomePageMotion({
    rootRef,
    routeMapRef,
    lifecycleSectionRef,
    rolesRef,
    suiteRef,
    aiRef,
  });

  useEffect(() => {
    document.title = HOME_PAGE_TITLE;
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute('content', HOME_META_DESCRIPTION);
    }
  }, []);

  const signInHref = isAuthenticated ? getDashboardHref() : getSignInHref();
  const signInLabel = isAuthenticated ? 'Open dashboard' : 'Sign in';
  const signInIsExternal = signInHref.startsWith('http');
  const demoHref = getDemoScheduleHref();
  const demoIsExternal =
    demoHref.startsWith('http') || demoHref.startsWith('mailto:');

  return (
    <div className="min-h-screen bg-[var(--se-porcelain)] text-[var(--se-ink)]">
      <MarketingSiteHeader isAuthenticated={isAuthenticated} />

      <main ref={rootRef} className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <section className="grid gap-10 py-10 lg:grid-cols-2 lg:items-center lg:py-14">
          <div data-home-hero>
            <h1 className="text-3xl font-semibold tracking-tight text-[var(--se-ink)] sm:text-4xl">
              One platform for the many responsibilities of education leadership.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-[var(--se-ink-muted)] sm:text-lg">
              Strat-Edu brings people, processes, and resources into one
              administrative workspace—connecting evaluations, communications,
              consent workflows, budgets, inventory, and organizational
              management.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {demoIsExternal ? (
                <a href={demoHref}>
                  <Button>Schedule a guided demo</Button>
                </a>
              ) : (
                <Link to={demoHref}>
                  <Button>Schedule a guided demo</Button>
                </Link>
              )}
              <a href={`#${PLATFORM_SUITE_ANCHOR}`}>
                <Button variant="secondary">Explore the platform</Button>
              </a>
            </div>
            <ul className="mt-10 grid gap-3 sm:grid-cols-2">
              {HERO_PILLARS.map(pillar => (
                <li
                  key={pillar.label}
                  data-home-pillar
                  className="rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] px-4 py-3"
                >
                  <p className="text-sm font-semibold text-[var(--se-ink)]">
                    {pillar.label}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-[var(--se-ink-muted)]">
                    {pillar.detail}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div data-home-route-panel>
            <HomeRouteMap ref={routeMapRef} />
          </div>
        </section>

        <section
          ref={suiteRef}
          id={PLATFORM_SUITE_ANCHOR}
          className="mt-20 scroll-mt-24"
          aria-labelledby="home-suite-heading"
        >
          <h2
            id="home-suite-heading"
            className="text-xl font-semibold text-[var(--se-ink)]"
          >
            A full suite for education leaders and administrative teams
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[var(--se-ink-muted)]">
            Strat-Edu supports educational institutions with connected tools for
            organization management, family communication, resource stewardship,
            teacher evaluation, student interventions, and reporting—without
            replacing professional judgment in the classroom.
          </p>
          <div className="mt-10 space-y-12">
            {PLATFORM_SUITE.map(area => (
              <div key={area.id} data-home-suite-area>
                <h3 className="text-base font-semibold text-[var(--se-ink)]">
                  {area.title}
                </h3>
                <p className="mt-2 max-w-3xl text-sm text-[var(--se-ink-muted)]">
                  {area.summary}
                </p>
                <ul className="mt-5 grid gap-4 sm:grid-cols-2">
                  {area.capabilities.map(cap => (
                    <li
                      key={cap.title}
                      data-home-suite-capability
                      className="rounded-md border border-[var(--se-rule)] bg-white p-5"
                    >
                      <h4 className="text-sm font-semibold text-[var(--se-ink)]">
                        {cap.title}
                      </h4>
                      <p className="mt-2 text-sm leading-relaxed text-[var(--se-ink-muted)]">
                        {cap.description}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section
          ref={rolesRef}
          className="mt-20 scroll-mt-24"
          aria-labelledby="home-roles-heading"
        >
          <h2
            id="home-roles-heading"
            className="text-xl font-semibold text-[var(--se-ink)]"
          >
            Built for the people who run educational institutions
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-[var(--se-ink-muted)]">
            Each role sees the tasks and data appropriate to their
            responsibility—whether they lead a district, manage a school,
            evaluate staff, teach, or participate through the parent portal.
          </p>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ROLE_BREAKDOWN.map(role => (
              <li
                key={role.id}
                data-home-role
                className="flex flex-col rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] p-5"
              >
                <h3 className="text-sm font-semibold text-[var(--se-ink)]">
                  {role.title}
                </h3>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[var(--se-route-teal)]">
                  {role.scope}
                </p>
                <ul className="mt-4 flex-1 space-y-2">
                  {role.responsibilities.map(item => (
                    <li
                      key={item}
                      className="text-sm leading-snug text-[var(--se-ink-muted)]"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>

        <HomeLifecycleSection
          activeStage={activeStage}
          sectionRef={lifecycleSectionRef}
        />

        <section
          ref={aiRef}
          className="mt-20 scroll-mt-24 rounded-md border border-[var(--se-rule)] bg-[var(--se-midnight)] px-6 py-10 text-white sm:px-10"
          aria-labelledby="home-ai-heading"
        >
          <h2 id="home-ai-heading" className="text-xl font-semibold">
            Optional AI assist for evaluation scoring
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-white/70">
            AI support applies to the teacher evaluation workflow only. It is
            optional, controlled by administrators, and always subject to human
            review—it does not run across announcements, budgets, inventory, or
            other modules.
          </p>
          <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AI_PIPELINE_STEPS.map(step => (
              <li
                key={step.step}
                data-home-ai-step
                className="rounded-md border border-white/15 bg-white/5 p-4"
              >
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--se-route-amber)]">
                  Step {step.step}
                </span>
                <h3 className="mt-2 text-sm font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/75">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-16 flex flex-col items-start gap-4 rounded-md border border-[var(--se-rule)] bg-[var(--se-porcelain-panel)] p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--se-ink)]">
              See Strat-Edu with your team
            </h2>
            <p className="mt-1 text-sm text-[var(--se-ink-muted)]">
              Schedule a guided walkthrough of the modules your organization
              would use, or sign in if you already have access.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {demoIsExternal ? (
              <a href={demoHref}>
                <Button>Schedule a guided demo</Button>
              </a>
            ) : (
              <Link to={demoHref}>
                <Button>Schedule a guided demo</Button>
              </Link>
            )}
            {signInIsExternal ? (
              <a href={signInHref}>
                <Button variant="secondary">{signInLabel}</Button>
              </a>
            ) : (
              <Link to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.LOGIN}>
                <Button variant="secondary">{signInLabel}</Button>
              </Link>
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-[var(--se-rule)] py-8">
        <p className="text-center text-xs text-[var(--se-ink-muted)]">
          Strat-Edu · Education administration platform for institutions,
          leaders, and administrative teams
        </p>
      </footer>
    </div>
  );
}
